import React from "react";
import { Link, LinkProps } from "react-router-dom";

import * as Styled from "./auth.styled";
import { PasswordInput } from "./password-input";

export const EnvelopeIcon = () => (
  <svg
    width="64"
    height="48"
    viewBox="0 0 64 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M4 14L32 30L60 14V40C60 41.1 59.1 42 58 42H6C4.9 42 4 41.1 4 40V14Z"
      fill="#BFD9F0"
    />
    <path
      d="M4 10C4 7.79 5.79 6 8 6H56C58.21 6 60 7.79 60 10V14L32 30L4 14V10Z"
      fill="#E8F1FB"
      stroke="#065FA6"
      strokeWidth="2"
    />
    <path
      d="M4 14V40C4 41.1 4.9 42 6 42H58C59.1 42 60 41.1 60 40V14"
      stroke="#065FA6"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

type AuthCardProps = {
  title: string;
  children: React.ReactNode;
  size?: "default" | "compact";
  icon?: React.ReactNode;
};

export const AuthCard = ({
  title,
  children,
  size = "default",
  icon,
}: AuthCardProps) => (
  <Styled.Card $size={size} data-auth-card>
    {icon ? <Styled.CardIcon>{icon}</Styled.CardIcon> : null}
    <Styled.Title>{title}</Styled.Title>
    {children}
  </Styled.Card>
);

type AuthTextFieldProps = {
  id: string;
  label: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const AuthTextField = ({
  id,
  label,
  ...inputProps
}: AuthTextFieldProps) => (
  <Styled.Field htmlFor={id}>
    {label}
    <Styled.Input id={id} {...inputProps} />
  </Styled.Field>
);

type AuthPasswordFieldProps = {
  id: string;
  label: string;
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  autoComplete: string;
};

export const AuthPasswordField = ({
  id,
  label,
  ...passwordProps
}: AuthPasswordFieldProps) => (
  <Styled.Field htmlFor={id}>
    {label}
    <PasswordInput id={id} {...passwordProps} />
  </Styled.Field>
);

type AuthSubmitButtonProps = {
  loading?: boolean;
  loadingLabel: string;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const AuthSubmitButton = ({
  loading,
  loadingLabel,
  children,
  ...buttonProps
}: AuthSubmitButtonProps) => (
  <Styled.Button
    {...buttonProps}
    type="submit"
    disabled={loading || buttonProps.disabled}
  >
    {loading ? loadingLabel : children}
  </Styled.Button>
);

export const AuthLinkRow = ({ children }: { children: React.ReactNode }) => (
  <Styled.LinkRow>{children}</Styled.LinkRow>
);

export const AuthLink = ({ children, ...linkProps }: LinkProps) => (
  <Link {...linkProps}>{children}</Link>
);

export const AuthPrimaryLink = ({ children, ...linkProps }: LinkProps) => (
  <Styled.ButtonLink {...linkProps}>{children}</Styled.ButtonLink>
);

const AlertSuccessIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M14.6673 8.00026C14.6673 4.31836 11.6825 1.3336 8.00065 1.3336C4.31875 1.3336 1.33398 4.31836 1.33398 8.00026C1.33398 11.6821 4.31875 14.6669 8.00065 14.6669C11.6825 14.6669 14.6673 11.6821 14.6673 8.00026Z"
      fill="#06797F"
      stroke="#06797F"
      strokeWidth="1.5"
    />
    <path
      d="M5.33398 8L7.33398 10L10.6673 6"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type AuthAlertProps = {
  children: React.ReactNode;
  onClose: () => void;
  tone?: "error" | "success";
  action?: React.ReactNode;
};

export const AuthAlert = ({
  children,
  onClose,
  tone = "error",
  action,
}: AuthAlertProps) => (
  <Styled.Alert role={tone === "error" ? "alert" : "status"} $tone={tone}>
    <Styled.AlertIcon $tone={tone}>
      {tone === "success" ? <AlertSuccessIcon /> : "●"}
    </Styled.AlertIcon>
    <Styled.AlertContent>
      <span>{children}</span>
      {action}
    </Styled.AlertContent>
    <Styled.AlertClose type="button" onClick={onClose} aria-label="Close alert">
      ×
    </Styled.AlertClose>
  </Styled.Alert>
);
